import { createFileRoute } from "@tanstack/react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { getTutors } from "@/api/tutors.ts";
import { styled } from "@linaria/react";
import { t } from "@/themes/theme.ts";
import { CardClaimedChats } from "@/components/cards/CardClaimedChats.tsx";
import { CardUnclaimedChats } from "@/components/cards/CardUnclaimedChats.tsx";
import { useUserStore } from "@/stores/useUserStore.ts";
import { type Tutor, type TutorId, TutorStatus } from "@/types/tutor.ts";
import { CardAllWelcome } from "@/components/cards/CardAllWelcome.tsx";
import { useEffect, useMemo } from "react";
import { motion } from "motion/react";
import { H1 } from "@/components/pages/shared.ts";
import { AI_TUTOR_ID } from "@/constants/settings.ts";
import { INVALID_TUTOR_ID, IsAiTutorId } from "@/helpers/aiTutor.ts";
import type { DataTag } from "@tanstack/query-core";
import { useActiveSessionStore } from "@/stores/useActiveSessionStore.ts";
import type { FlowGeneratorSession } from "@/types/flow.ts";
import { getLessonDashboardStats } from "@/api/admin.ts";
import { CardStats } from "@/components/cards/CardStats.tsx";
import { useSignalStore } from "@/stores/useSignalrStore.ts";
import { toastAndError } from "@/helpers/toast.ts";

export const Route = createFileRoute("/all-chats")({
  component: AllChats,
  head: () => ({
    meta: [{ title: "All Chats | Eedi Tutor Dashboard" }],
  }),
});

const HeaderWrapper = styled.div`
  display: flex;
  padding-bottom: 25px;
`;

const Background = styled(motion.div)`
  background-color: ${t.brandSecondary};
  width: 100%;
  min-height: 100%;
  padding: 1rem;
`;

const CardWrapper = styled.div`
  display: flex;
  justify-content: stretch;
  width: 100%;
  gap: 20px;
  flex-wrap: wrap;
  container-type: inline-size;
  padding-bottom: 100px;
`;

const defaultTutor: Tutor = {
  tutorId: INVALID_TUTOR_ID,
  firstName: "Unknown",
  lastName: "Tutor",
  authorPermissions: [],
  dateCreated: "",
  dateDeleted: null,
  defaultClaimMessage: "",
  description: "",
  email: "",
  imageURL: "",
  isAuthor: false,
  isOnline: true,
  isContractor: false,
  isSuperAdmin: false,
  isAutoInterventionsEnabled: false,
  dateLastLoggedIn: "",
  id: INVALID_TUTOR_ID,
  userId: INVALID_TUTOR_ID,
  userName: "",
  userStatus: TutorStatus.Offline,
};

interface KnownTutor extends Tutor {
  missedCount?: number;
}

const mergeKnownTutors = (
  previous: Array<KnownTutor> | undefined,
  fresh: Array<Tutor>,
): Array<KnownTutor> => {
  const prevMap = new Map<number, KnownTutor>(
    previous?.map((tutor) => [tutor.tutorId, tutor]) ?? [],
  );
  const freshMap = new Map<number, Tutor>(fresh.map((tutor) => [tutor.tutorId, tutor]));

  const merged = new Map<number, KnownTutor>();

  for (const tutor of fresh) {
    const prev = prevMap.get(tutor.tutorId);
    if (tutor.isOnline) {
      merged.set(tutor.tutorId, {
        ...tutor,
        missedCount: 0,
      });
    } else {
      const missedCount = (prev?.isOnline ? (prev.missedCount ?? 0) : 5) + 1;
      merged.set(tutor.tutorId, {
        ...tutor,
        isOnline: missedCount < 5,
        missedCount,
      });
    }
  }

  if (previous) {
    for (const prev of previous) {
      if (!freshMap.has(prev.tutorId)) {
        const missedCount = (prev.isOnline ? (prev.missedCount ?? 0) : 5) + 1;
        merged.set(prev.tutorId, {
          ...prev,
          isOnline: missedCount < 5,
          missedCount,
        });
      }
    }
  }

  return [...merged.values()];
};

const ACTIVE_TUTORS_KEY = ["activeTutors"] as DataTag<["activeTutors"], Array<KnownTutor>>;

function AllChats() {
  const { tutorId, settings } = useUserStore();
  const {
    activeSessions,
    helpRequestedSessions,
    AddNewSession,
    AddHelpRequestedSession,
    ClearSessions,
  } = useActiveSessionStore();
  const queryClient = useQueryClient();
  const { signalr } = useSignalStore();

  const tutorQuery = useQuery({
    queryFn: async () => {
      const response = await getTutors();
      return mergeKnownTutors(queryClient.getQueryData(ACTIVE_TUTORS_KEY), response.data);
    },
    queryKey: ACTIVE_TUTORS_KEY,
    refetchInterval: 1000 * 60,
    refetchIntervalInBackground: true,
    staleTime: 1000 * 60,
    enabled: tutorId !== -1,
  });

  const dashboardQuery = useQuery({
    queryFn: () => getLessonDashboardStats(),
    queryKey: ["lessonDashboardStats"],
    refetchInterval: 1000 * 60,
    refetchIntervalInBackground: true,
    staleTime: 1000 * 60,
    enabled: tutorId !== -1,
  });

  useEffect(() => {
    if (!signalr) return;

    const removeCallback = signalr.OnStatusCallback(() => {
      dashboardQuery.refetch().catch((e) => {
        toastAndError(
          "Attempted to refresh dashboard status as signalr went down, but failed. Your internet may be down.",
          e,
        );
      });
    });

    return () => {
      removeCallback();
    };
  }, [signalr, dashboardQuery]);

  const knownTutors = useMemo(
    () => new Map(tutorQuery.data?.map((known) => [known.tutorId, known])),
    [tutorQuery.data],
  );
  const tutorUserIdToId = useMemo(
    () => new Map(tutorQuery.data?.map((known) => [known.userId, known.tutorId as TutorId])),
    [tutorQuery.data],
  );

  const activeSessionCount = useMemo(() => {
    return [...activeSessions.values()].reduce(
      (total, sessions) => total + (sessions?.length ?? 0),
      0,
    );
  }, [activeSessions]);

  useEffect(() => {
    if (!dashboardQuery.data || tutorUserIdToId.size === 0) return;

    let sessions = dashboardQuery.data.activeFlowSessions;
    ClearSessions();

    for (const session of sessions) {
      if (session.tutorUserId) {
        const tutorId = tutorUserIdToId.get(session.tutorUserId);

        if (tutorId === undefined) {
          console.warn("Got undefined tutorId for session, setting to ", INVALID_TUTOR_ID, session);
        }

        AddNewSession(tutorId ?? INVALID_TUTOR_ID, session as Partial<FlowGeneratorSession>);
      } else if (
        session.isHelpRequested ||
        session.isProactiveIntervention ||
        !!session.dateProcessed
      )
        AddHelpRequestedSession(session as Partial<FlowGeneratorSession>);
    }
  }, [
    dashboardQuery.data,
    tutorQuery.data,
    AddNewSession,
    AddHelpRequestedSession,
    ClearSessions,
    tutorUserIdToId,
  ]);

  const onlineTutors = useMemo(() => {
    const onlineTutors = new Map<TutorId, boolean>();

    for (const key of [...activeSessions.keys()]) {
      onlineTutors.set(key, true);
    }

    knownTutors.forEach((tutor) => {
      if (tutor.isOnline) onlineTutors.set(tutor.tutorId, true);
    });

    return onlineTutors;
  }, [activeSessions, knownTutors]);

  return (
    <Background layout>
      <HeaderWrapper>
        <H1>All Chats</H1>
      </HeaderWrapper>
      <CardWrapper>
        {settings.capacity > 0 && settings.status !== TutorStatus.Offline ? (
          <CardClaimedChats
            tutor={knownTutors.get(tutorId) ?? defaultTutor}
            activeInterventions={activeSessions.get(tutorId) ?? []}
          />
        ) : (
          <CardAllWelcome />
        )}
        <CardUnclaimedChats sessions={helpRequestedSessions} />
        <CardStats
          dashboardStats={dashboardQuery.data}
          claimedSessionCount={activeSessionCount}
          tutorsOnlineCount={onlineTutors.size}
        />
        <CardClaimedChats
          tutor={{
            tutorId: AI_TUTOR_ID,
            firstName: "Eedi",
            lastName: "AI",
          }}
          activeInterventions={activeSessions.get(AI_TUTOR_ID) ?? []}
        />
        {[...onlineTutors.keys()].map((activeTutorId) => {
          if (activeTutorId === tutorId || IsAiTutorId(activeTutorId)) return null;

          const tutor = knownTutors.get(activeTutorId) ?? {
            ...defaultTutor,
            tutorId: activeTutorId,
          };

          return (
            <CardClaimedChats
              key={activeTutorId}
              tutor={tutor}
              activeInterventions={activeSessions.get(activeTutorId) ?? []}
            />
          );
        })}
      </CardWrapper>
    </Background>
  );
}
